import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Breadcrumbs,
  Link,
  FormControl,
  FormLabel,
  Select,
  MenuItem,
  InputLabel,
  Radio,
  RadioGroup,
  FormControlLabel,
  Paper,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Switch
} from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import SaveIcon from '@mui/icons-material/Save';
import PrintIcon from '@mui/icons-material/Print';
import PreviewIcon from '@mui/icons-material/Preview';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import ShareIcon from '@mui/icons-material/Share';
import Layout from '../../components/layout/Layout';
import { toast } from 'react-hot-toast';
import printSettingsService from '../../services/printSettingsService';
import companySettingsService from '../../services/companySettingsService';

const PrintSettingsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [selectedPaperSize, setSelectedPaperSize] = useState('A4'); // First level: Paper size
  const [selectedFormat, setSelectedFormat] = useState('A4'); // Second level: Actual format (e.g., A4, 3_INCH_SIMPLE, 3_INCH_GST_DETAILS)
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [enableWhatsappShare, setEnableWhatsappShare] = useState(false);
  const [updatingWhatsappShare, setUpdatingWhatsappShare] = useState(false);
  const [directPrint, setDirectPrint] = useState(false);
  const [updatingDirectPrint, setUpdatingDirectPrint] = useState(false);
  const [previewPrint, setPreviewPrint] = useState(false);
  const [updatingPreviewPrint, setUpdatingPreviewPrint] = useState(false);
  const [showPrintSchemeSelection, setShowPrintSchemeSelection] = useState(false);
  const [updatingPrintSchemeSelection, setUpdatingPrintSchemeSelection] = useState(false);
  const [planFeatures, setPlanFeatures] = useState(null);
  const [electronPrinters, setElectronPrinters] = useState([]);
  const [selectedElectronPrinter, setSelectedElectronPrinter] = useState('');
  const [loadingPrinters, setLoadingPrinters] = useState(false);
  const [savingElectronPrinter, setSavingElectronPrinter] = useState(false);


  // Format options by paper size
  const formatOptions = {
    'A5': [
      { value: 'A5_FORMAT_1', label: 'A5 Format 1 (Landscape Invoice)', description: 'Landscape A5 invoice format (210mm × 148mm) based on compact tax invoice layout' },
      { value: 'A5_FORMAT_2_DOUBLE', label: 'A5 Format 2 Double (2 copies on A5)', description: 'Two identical A6-sized invoices side-by-side (left & right) on one A5 landscape page—one for customer, one for business.' },
      { value: 'A5_FORMAT_2_DOUBLE_GST', label: 'A5 Format 3 Double With GST (2 copies on A5)', description: 'Two identical A6-sized invoices with GST% column and GST summary table—includes Goods Value, CGST, SGST, IGST, GST totals.' }
    ],
    'A4': [
      { value: 'A4', label: 'A4 Standard', description: 'Standard A4 format with full details' },
      { value: 'A4_FORMAT_2', label: 'A4 Format 2 (Detailed GST)', description: 'GST breakdown with CGST/SGST columns and HSN codes' },
      { value: 'A4_FORMAT_3', label: 'A4 Format 3 (Boxed Professional)', description: 'Professional boxed layout with amount in words and signatory' },
      { value: 'A4_FORMAT_4', label: 'A4 Format 4 (Legacy)', description: 'Traditional layout with Receiver and Bank details side-by-side' }
    ],
    'A6': [
      { value: 'A6_FORMAT_1', label: 'A6 Format 1 (Bill details only)', description: 'Customer, payment, invoice no, date, items table, subtotal, discount and total only. No company header or terms footer.' }
    ],
    '3_INCH': [
      { value: '3_INCH_SIMPLE', label: '3-inch (Simple)', description: 'Basic receipt format without GST breakdown' },
      { value: '3_INCH_GST_DETAILS', label: '3-inch (GST Details)', description: 'Includes detailed GST breakdown table by tax rate' }
    ],
    '2_INCH': [
      { value: '2_INCH_SIMPLE', label: '2-inch (Standard)', description: 'Standard receipt format for 58mm thermal printers' }
    ]
  };

  const handleClosePreview = useCallback(() => {
    setPreviewOpen(false);
    if (previewUrl) {
      window.URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
  }, [previewUrl]);

  // Load current print settings (including WhatsApp share)
  useEffect(() => {
    loadPrintSettings();
    if (window.electronAPI) {
      loadElectronPrinters();
    }
  }, []);

  const loadElectronPrinters = async () => {
    try {
      setLoadingPrinters(true);
      const printers = await window.electronAPI.getPrinters();
      setElectronPrinters(printers || []);
      const defaultName = await window.electronAPI.getDefaultPrinter();
      setSelectedElectronPrinter(defaultName || '');
    } catch (e) {
      console.error('Failed to load electron printers:', e);
    } finally {
      setLoadingPrinters(false);
    }
  };

  const handleSaveElectronPrinter = async () => {
    try {
      setSavingElectronPrinter(true);
      await window.electronAPI.setDefaultPrinter(selectedElectronPrinter);
      toast.success('Desktop printer setting saved locally');
    } catch (e) {
      console.error('Error saving electron printer:', e);
      toast.error('Failed to save printer setting');
    } finally {
      setSavingElectronPrinter(false);
    }
  };

  // Reactively track plan features for WhatsApp Share visibility (same as CompanySettings / Navigation)
  useEffect(() => {
    if (typeof window !== 'undefined' && window.__PLAN_FEATURES__) {
      setPlanFeatures(window.__PLAN_FEATURES__);
    }
    const handlePlanFeaturesUpdated = (event) => {
      const next = event?.detail ?? (typeof window !== 'undefined' && window.__PLAN_FEATURES__) ?? null;
      setPlanFeatures(next);
    };
    if (typeof window !== 'undefined') {
      window.addEventListener('planFeaturesUpdated', handlePlanFeaturesUpdated);
    }
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('planFeaturesUpdated', handlePlanFeaturesUpdated);
      }
    };
  }, []);

  // Handle Escape key to close preview dialog
  useEffect(() => {
    if (!previewOpen) return;

    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        handleClosePreview();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [previewOpen, handleClosePreview]);

  const getLocationId = () => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        return user.location?.id;
      } catch (e) {
        console.error('Error parsing user from localStorage', e);
      }
    }
    return null;
  };

  const loadPrintSettings = async () => {
    try {
      setLoading(true);
      setErrorMessage('');
      const locationId = getLocationId();
      const response = await printSettingsService.getPrintSettings(locationId);
      if (response.success) {
        // Handle print format
        if (response.printFormat) {
          let format = response.printFormat.toUpperCase();
          if (format === '3_INCH') {
            format = '3_INCH_SIMPLE'; // Migrate old format to new name
          }
          setSelectedFormat(format);

          // Determine paper size based on format
          if (format.startsWith('3_INCH')) {
            setSelectedPaperSize('3_INCH');
          } else if (format.startsWith('2_INCH')) {
            setSelectedPaperSize('2_INCH');
          } else if (format.startsWith('A6')) {
            setSelectedPaperSize('A6');
          } else if (format.startsWith('A5')) {
            setSelectedPaperSize('A5');
          } else {
            setSelectedPaperSize('A4');
          }
        } else {
          // Default to A4 if not set
          setSelectedFormat('A4');
          setSelectedPaperSize('A4');
        }

        // Handle WhatsApp share
        if (typeof response.enableWhatsappShare === 'boolean') {
          setEnableWhatsappShare(response.enableWhatsappShare);
        }

        // Handle Direct Print
        if (typeof response.directPrint === 'boolean') {
          setDirectPrint(response.directPrint);
        }

        // Handle Preview & Print
        if (typeof response.previewPrint === 'boolean') {
          setPreviewPrint(response.previewPrint);
        }

        // Handle Print Scheme Selection
        if (typeof response.showPrintSchemeSelection === 'boolean') {
          setShowPrintSchemeSelection(response.showPrintSchemeSelection);
        }
      } else {
        // Default to A4 if not set
        setSelectedFormat('A4');
        setSelectedPaperSize('A4');
      }
    } catch (error) {
      console.error('Error loading print settings:', error);
      setErrorMessage('Failed to load print settings');
      toast.error('Failed to load print settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setSuccessMessage('');
      setErrorMessage('');

      const locationId = getLocationId();
      const response = await printSettingsService.updatePrintSettings(selectedFormat, locationId);

      if (response.success) {
        setSuccessMessage('Print format updated successfully! All future receipts will use this format.');
        toast.success('Print format updated successfully');
      } else {
        setErrorMessage(response.message || 'Failed to update print format');
        toast.error(response.message || 'Failed to update print format');
      }
    } catch (error) {
      console.error('Error updating print settings:', error);
      const errorMsg = error.response?.data?.message || 'Failed to update print format';
      setErrorMessage(errorMsg);
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const handlePaperSizeChange = (paperSize) => {
    setSelectedPaperSize(paperSize);
    // Auto-select first format of the selected paper size
    if (formatOptions[paperSize] && formatOptions[paperSize].length > 0) {
      setSelectedFormat(formatOptions[paperSize][0].value);
    }
  };

  const handlePreview = async (format) => {
    try {
      setPreviewing(true);
      setErrorMessage('');

      const pdfBlob = await printSettingsService.previewPrintFormat(format);

      // Create URL for the blob
      const url = window.URL.createObjectURL(pdfBlob);
      setPreviewUrl(url);
      setPreviewOpen(true);

      toast.success('Preview generated');
    } catch (error) {
      console.error('Error generating preview:', error);
      const errorMsg = error.response?.data?.message || 'Failed to generate preview';
      setErrorMessage(errorMsg);
      toast.error(errorMsg);
    } finally {
      setPreviewing(false);
    }
  };

  const handleWhatsappShareToggle = async (newValue) => {
    try {
      setUpdatingWhatsappShare(true);

      // Validation: Can't enable WhatsApp share if directPrint is enabled
      if (newValue && directPrint) {
        toast.error('Cannot enable WhatsApp Share when Direct Print is enabled. Please disable Direct Print first.');
        setUpdatingWhatsappShare(false);
        return;
      }

      // Update local state immediately for better UX
      setEnableWhatsappShare(newValue);

      // Update via print settings service (handles location-specific settings)
      const locationId = getLocationId();
      await printSettingsService.updatePrintSettings({ enableWhatsappShare: newValue }, locationId);

      toast.success(`WhatsApp share ${newValue ? 'enabled' : 'disabled'}`);
    } catch (error) {
      console.error('Error updating WhatsApp share setting:', error);
      // Revert local state on error
      setEnableWhatsappShare(!newValue);
      const errorMsg = error.response?.data?.message || 'Failed to update WhatsApp share setting';
      toast.error(errorMsg);
    } finally {
      setUpdatingWhatsappShare(false);
    }
  };

  const handleDirectPrintToggle = async (newValue) => {
    try {
      setUpdatingDirectPrint(true);

      // Validation: Can't enable directPrint if previewPrint or enableWhatsappShare is enabled
      if (newValue) {
        if (previewPrint) {
          toast.error('Cannot enable Direct Print when Preview & Print is enabled. Please disable Preview & Print first.');
          setUpdatingDirectPrint(false);
          return;
        }
        if (enableWhatsappShare) {
          toast.error('Cannot enable Direct Print when WhatsApp Share is enabled. Please disable WhatsApp Share first.');
          setUpdatingDirectPrint(false);
          return;
        }
      }

      // Update local state immediately for better UX
      setDirectPrint(newValue);

      // Update via print settings service
      const locationId = getLocationId();
      await printSettingsService.updatePrintSettings({ directPrint: newValue }, locationId);

      toast.success(`Direct Print ${newValue ? 'enabled' : 'disabled'}`);
    } catch (error) {
      console.error('Error updating Direct Print setting:', error);
      // Revert local state on error
      setDirectPrint(!newValue);
      const errorMsg = error.response?.data?.message || 'Failed to update Direct Print setting';
      toast.error(errorMsg);
    } finally {
      setUpdatingDirectPrint(false);
    }
  };

  const handlePreviewPrintToggle = async (newValue) => {
    try {
      setUpdatingPreviewPrint(true);

      // Validation: Can't enable previewPrint if directPrint is enabled
      if (newValue && directPrint) {
        toast.error('Cannot enable Preview & Print when Direct Print is enabled. Please disable Direct Print first.');
        setUpdatingPreviewPrint(false);
        return;
      }

      // Update local state immediately for better UX
      setPreviewPrint(newValue);

      // Update via print settings service
      const locationId = getLocationId();
      await printSettingsService.updatePrintSettings({ previewPrint: newValue }, locationId);

      toast.success(`Preview & Print ${newValue ? 'enabled' : 'disabled'}`);
    } catch (error) {
      console.error('Error updating Preview & Print setting:', error);
      // Revert local state on error
      setPreviewPrint(!newValue);
      const errorMsg = error.response?.data?.message || 'Failed to update Preview & Print setting';
      toast.error(errorMsg);
    } finally {
      setUpdatingPreviewPrint(false);
    }
  };

  const handlePrintSchemeSelectionToggle = async (newValue) => {
    try {
      setUpdatingPrintSchemeSelection(true);

      // Validation: Can't enable if directPrint is enabled
      if (newValue && directPrint) {
        toast.error('Cannot enable Print Scheme Selection when Direct Print is enabled.');
        setUpdatingPrintSchemeSelection(false);
        return;
      }

      // Update local state immediately for better UX
      setShowPrintSchemeSelection(newValue);

      // Update via print settings service
      const locationId = getLocationId();
      await printSettingsService.updatePrintSettings({ showPrintSchemeSelection: newValue }, locationId);

      toast.success(`Print Scheme Selection ${newValue ? 'enabled' : 'disabled'}`);
    } catch (error) {
      console.error('Error updating Print Scheme Selection setting:', error);
      // Revert local state on error
      setShowPrintSchemeSelection(!newValue);
      const errorMsg = error.response?.data?.message || 'Failed to update Print Scheme Selection setting';
      toast.error(errorMsg);
    } finally {
      setUpdatingPrintSchemeSelection(false);
    }
  };

  return (
    <Layout>
      <Container maxWidth="md" sx={{ py: 4 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          sx={{ mb: 3 }}
        >
          <Link component={RouterLink} to="/settings" color="inherit">
            Settings
          </Link>
          <Typography color="text.primary">Print Settings</Typography>
        </Breadcrumbs>

        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
          <PrintIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          <Typography variant="h4" component="h1">
            Print Settings
          </Typography>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
                Select Print Format
              </Typography>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Select the paper size from the dropdown, then choose the format template. This setting will apply to all print operations.
              </Typography>

              <Divider sx={{ my: 3 }} />

              {/* Step 1: Select Paper Size */}
              <Box sx={{ mb: 4 }}>
                <FormControl fullWidth>
                  <InputLabel id="paper-size-label">Select Paper Size</InputLabel>
                  <Select
                    labelId="paper-size-label"
                    id="paper-size-select"
                    value={selectedPaperSize}
                    label="Select Paper Size"
                    onChange={(e) => handlePaperSizeChange(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                      }
                    }}
                  >
                    <MenuItem value="A4">A4 (210mm × 297mm)</MenuItem>
                    <MenuItem value="A5">A5 (210mm × 148mm)</MenuItem>
                    <MenuItem value="A6">A6 (105mm × 148mm)</MenuItem>
                    <MenuItem value="3_INCH">3-inch (80mm width)</MenuItem>
                    <MenuItem value="2_INCH">2-inch (58mm width)</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Step 2: Select Format Template */}
              {selectedPaperSize && formatOptions[selectedPaperSize] && (
                <Box sx={{ mb: 4 }}>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                    Step 2: Select Format Template
                  </Typography>
                  <FormControl component="fieldset" fullWidth>
                    <RadioGroup
                      value={selectedFormat}
                      onChange={(e) => setSelectedFormat(e.target.value)}
                    >
                      {formatOptions[selectedPaperSize].map((format) => (
                        <Paper
                          key={format.value}
                          elevation={selectedFormat === format.value ? 3 : 0}
                          sx={{
                            p: 2,
                            mb: 2,
                            border: selectedFormat === format.value ? '2px solid' : '1px solid',
                            borderColor: selectedFormat === format.value ? 'primary.main' : 'divider',
                            borderRadius: 2,
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            '&:hover': {
                              backgroundColor: 'action.hover',
                              transform: 'translateY(-2px)'
                            },
                            '&:focus-within': {
                              borderColor: 'primary.main',
                              boxShadow: 2
                            }
                          }}
                          onClick={() => setSelectedFormat(format.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              setSelectedFormat(format.value);
                            }
                          }}
                          tabIndex={0}
                          role="button"
                          aria-label={`Select ${format.label}`}
                        >
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%' }}>
                            <FormControlLabel
                              value={format.value}
                              control={<Radio />}
                              label={
                                <Box>
                                  <Typography variant="body1" fontWeight="bold">
                                    {format.label}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    {format.description}
                                  </Typography>
                                </Box>
                              }
                              sx={{ m: 0, flex: 1 }}
                            />
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={previewing ? <CircularProgress size={16} /> : <PreviewIcon />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handlePreview(format.value);
                                }}
                                disabled={previewing}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.stopPropagation();
                                    handlePreview(format.value);
                                  }
                                }}
                              >
                                Preview
                              </Button>
                              <Button
                                variant="contained"
                                size="small"
                                startIcon={<EditIcon />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/settings/print/customize?baseFormat=${format.value}`);
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.stopPropagation();
                                    navigate(`/settings/print/customize?baseFormat=${format.value}`);
                                  }
                                }}
                              >
                                Customize
                              </Button>
                            </Box>
                          </Box>
                        </Paper>
                      ))}
                    </RadioGroup>
                  </FormControl>
                </Box>
              )}

              {/* WhatsApp Share Settings – only when plan includes whatsapp_bill_share */}
              {(!planFeatures || planFeatures._treatAllEnabled || planFeatures.whatsapp_bill_share === true ||
                (typeof planFeatures.whatsapp_bill_share === 'string' && planFeatures.whatsapp_bill_share.toLowerCase() === 'true')) && (
                  <>
                    <Divider sx={{ my: 3 }} />
                    <Box sx={{ mb: 4 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <ShareIcon sx={{ fontSize: 24, color: 'primary.main' }} />
                        <Typography variant="h6" fontWeight="bold">
                          WhatsApp Share Settings
                        </Typography>
                      </Box>
                      <Card variant="outlined" sx={{ bgcolor: 'background.default' }}>
                        <CardContent>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="body1" fontWeight="medium" gutterBottom>
                                Enable WhatsApp Share
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Automatically open WhatsApp after successful billing to share the bill with customers
                              </Typography>
                            </Box>
                            <Box sx={{ ml: 2 }}>
                              {updatingWhatsappShare ? (
                                <CircularProgress size={24} />
                              ) : (
                                <Switch
                                  checked={enableWhatsappShare}
                                  onChange={(e) => handleWhatsappShareToggle(e.target.checked)}
                                  color="primary"
                                  disabled={updatingWhatsappShare}
                                />
                              )}
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    </Box>
                    <Divider sx={{ my: 3 }} />
                  </>
                )}

              {/* Print Behavior Settings Section */}
              <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <PrintIcon sx={{ fontSize: 24, color: 'primary.main' }} />
                  <Typography variant="h6" fontWeight="bold">
                    Print Behavior Settings
                  </Typography>
                </Box>

                {/* Preview & Print */}
                <Card variant="outlined" sx={{ bgcolor: 'background.default', mb: 2 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body1" fontWeight="medium" gutterBottom>
                          Preview & Print
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          After placing an order, show a preview dialog with bill details and a print button. Clicking print opens the print selection screen.
                        </Typography>
                        {directPrint && (
                          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                            ⚠️ Cannot enable when Direct Print is enabled
                          </Typography>
                        )}
                      </Box>
                      <Box sx={{ ml: 2 }}>
                        {updatingPreviewPrint ? (
                          <CircularProgress size={24} />
                        ) : (
                          <Switch
                            checked={previewPrint}
                            onChange={(e) => handlePreviewPrintToggle(e.target.checked)}
                            color="primary"
                            disabled={updatingPreviewPrint || directPrint}
                          />
                        )}
                      </Box>
                    </Box>
                  </CardContent>
                </Card>

                {/* Direct Print */}
                <Card variant="outlined" sx={{ bgcolor: 'background.default', mb: 2 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body1" fontWeight="medium" gutterBottom>
                          Direct Print
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          After saving an order, directly open the print selection screen without any dialog.
                        </Typography>
                        {(previewPrint || enableWhatsappShare) && (
                          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                            ⚠️ Cannot enable when Preview & Print or WhatsApp Share is enabled
                          </Typography>
                        )}
                      </Box>
                      <Box sx={{ ml: 2 }}>
                        {updatingDirectPrint ? (
                          <CircularProgress size={24} />
                        ) : (
                          <Switch
                            checked={directPrint}
                            onChange={(e) => handleDirectPrintToggle(e.target.checked)}
                            color="primary"
                            disabled={updatingDirectPrint || previewPrint || enableWhatsappShare}
                          />
                        )}
                      </Box>
                    </Box>
                  </CardContent>
                </Card>

                {/* Show Print Scheme Selection */}
                <Card variant="outlined" sx={{ bgcolor: 'background.default' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body1" fontWeight="medium" gutterBottom>
                          Show Print Scheme Selection
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Opens a dialog after each sale to choose the invoice format (A4, A5, Thermal etc.) before printing.
                        </Typography>
                        {directPrint && (
                          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                            ⚠️ Cannot enable when Direct Print is enabled
                          </Typography>
                        )}
                      </Box>
                      <Box sx={{ ml: 2 }}>
                        {updatingPrintSchemeSelection ? (
                          <CircularProgress size={24} />
                        ) : (
                          <Switch
                            checked={showPrintSchemeSelection}
                            onChange={(e) => handlePrintSchemeSelectionToggle(e.target.checked)}
                            color="primary"
                            disabled={updatingPrintSchemeSelection || directPrint}
                          />
                        )}
                      </Box>
                    </Box>
                  </CardContent>
                </Card>

                {/* Electron Printer Selection Section - Only in Desktop App */}
                {window.electronAPI && (
                  <>
                    <Divider sx={{ my: 3 }} />
                    <Box sx={{ mb: 4 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <PrintIcon sx={{ fontSize: 24, color: 'primary.main' }} />
                        <Typography variant="h6" fontWeight="bold">
                          Desktop Printer (Electron)
                        </Typography>
                      </Box>
                      <Card variant="outlined" sx={{ bgcolor: 'info.lighter' }}>
                        <CardContent>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            These settings apply only on this computer. Choose the printer to use for "Direct Print" in the desktop app.
                          </Typography>

                          <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} sm={8}>
                              <FormControl fullWidth size="small">
                                <InputLabel id="electron-printer-label">Select Printer</InputLabel>
                                <Select
                                  labelId="electron-printer-label"
                                  id="electron-printer-select"
                                  value={selectedElectronPrinter}
                                  label="Select Printer"
                                  onChange={(e) => setSelectedElectronPrinter(e.target.value)}
                                  disabled={loadingPrinters}
                                >
                                  <MenuItem value="">
                                    <em>Use Windows Default</em>
                                  </MenuItem>
                                  {electronPrinters.map((printer) => (
                                    <MenuItem key={printer.name} value={printer.name}>
                                      {printer.name} {printer.isDefault ? '(System Default)' : ''}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                              <Button
                                variant="contained"
                                color="info"
                                fullWidth
                                startIcon={savingElectronPrinter ? <CircularProgress size={20} /> : <SaveIcon />}
                                onClick={handleSaveElectronPrinter}
                                disabled={loadingPrinters || savingElectronPrinter}
                              >
                                Save Local
                              </Button>
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>
                    </Box>
                  </>
                )}
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Format Details */}
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                  Current Selection Details:
                </Typography>
                {selectedFormat === 'A4' ? (
                  <Box sx={{ mt: 1, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                    <Typography variant="body2">
                      <strong>Paper Size:</strong> A4 (210mm × 297mm / 8.27" × 11.69")
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      <strong>Format:</strong> A4 Standard
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      <strong>Best for:</strong> Standard office printers, detailed invoices with logos and full company information
                    </Typography>
                  </Box>
                ) : selectedFormat === 'A4_FORMAT_2' ? (
                  <Box sx={{ mt: 1, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                    <Typography variant="body2">
                      <strong>Paper Size:</strong> A4 (210mm × 297mm)
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      <strong>Format:</strong> Detailed GST (Includes GST breakdown columns in items table)
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      <strong>Best for:</strong> B2B invoices and detailed tax reporting
                    </Typography>
                  </Box>
                ) : selectedFormat === 'A4_FORMAT_3' ? (
                  <Box sx={{ mt: 1, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                    <Typography variant="body2">
                      <strong>Paper Size:</strong> A4 (210mm × 297mm)
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      <strong>Format:</strong> Boxed Professional (Modern boxed layout with amount in words)
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      <strong>Best for:</strong> Premium professional invoices with clear sections and signatory
                    </Typography>
                  </Box>
                ) : selectedFormat === 'A4_FORMAT_4' ? (
                  <Box sx={{ mt: 1, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                    <Typography variant="body2">
                      <strong>Paper Size:</strong> A4 (210mm × 297mm)
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      <strong>Format:</strong> Legacy (Side-by-side Layout)
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      <strong>Best for:</strong> Traditional businesses requiring bank details on header
                    </Typography>
                  </Box>
                ) : selectedFormat === 'A6_FORMAT_1' ? (
                  <Box sx={{ mt: 1, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                    <Typography variant="body2">
                      <strong>Paper Size:</strong> A6 (105mm × 148mm)
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      <strong>Format:</strong> Bill details only (Customer, payment, invoice no, date, items, subtotal, discount, total)
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      <strong>Best for:</strong> A6 paper with pre-printed header/footer; prints only bill content
                    </Typography>
                  </Box>
                ) : selectedFormat === 'A5_FORMAT_1' ? (
                  <Box sx={{ mt: 1, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                    <Typography variant="body2">
                      <strong>Paper Size:</strong> A5 (210mm × 148mm) Landscape
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      <strong>Format:</strong> Landscape compact invoice (Tax + totals layout)
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      <strong>Best for:</strong> A5 landscape printing for compact invoice sheets
                    </Typography>
                  </Box>
                ) : selectedFormat === 'A5_FORMAT_2_DOUBLE' ? (
                  <Box sx={{ mt: 1, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                    <Typography variant="body2">
                      <strong>Paper Size:</strong> A5 (210mm × 148mm) Landscape
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      <strong>Format:</strong> Two identical A6-sized invoices side-by-side (left &amp; right)—one for customer, one for business
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      <strong>Best for:</strong> Printing one bill with two copies on a single A5 landscape page
                    </Typography>
                  </Box>
                ) : selectedFormat === 'A5_FORMAT_2_DOUBLE_GST' ? (
                  <Box sx={{ mt: 1, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                    <Typography variant="body2">
                      <strong>Paper Size:</strong> A5 (210mm × 148mm) Landscape
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      <strong>Format:</strong> Two identical A6-sized invoices with GST% column in items and GST summary table (Goods Value, CGST, SGST, IGST, GST)
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      <strong>Best for:</strong> GST-registered businesses needing a tax summary on each copy
                    </Typography>
                  </Box>
                ) : selectedFormat === '3_INCH_SIMPLE' ? (
                  <Box sx={{ mt: 1, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                    <Typography variant="body2">
                      <strong>Paper Size:</strong> 3-inch (80mm width - Variable height)
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      <strong>Format:</strong> Simple (Basic receipt without GST breakdown)
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      <strong>Best for:</strong> Thermal printers, receipt machines, compact printing
                    </Typography>
                  </Box>
                ) : selectedFormat === '3_INCH_GST_DETAILS' ? (
                  <Box sx={{ mt: 1, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                    <Typography variant="body2">
                      <strong>Paper Size:</strong> 3-inch (80mm width - Variable height)
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      <strong>Format:</strong> GST Details (Includes GST breakdown table)
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      <strong>Best for:</strong> Thermal printers with detailed tax breakdown showing tax rates, taxable amounts, CGST, and SGST
                    </Typography>
                  </Box>
                ) : selectedFormat === '2_INCH_SIMPLE' ? (
                  <Box sx={{ mt: 1, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                    <Typography variant="body2">
                      <strong>Paper Size:</strong> 2-inch (58mm width)
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      <strong>Format:</strong> 2-inch Standard
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      <strong>Best for:</strong> Portable Bluetooth thermal printers (58mm)
                    </Typography>
                  </Box>
                ) : null}
              </Box>

              {/* Success/Error Notifications */}
              {successMessage && (
                <Alert severity="success" sx={{ mt: 3, mb: 1 }} onClose={() => setSuccessMessage('')}>
                  {successMessage}
                </Alert>
              )}
              {errorMessage && (
                <Alert severity="error" sx={{ mt: 3, mb: 1 }} onClose={() => setErrorMessage('')}>
                  {errorMessage}
                </Alert>
              )}

              {/* Save Button */}
              <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    // Reset to current saved format
                    loadPrintSettings();
                  }}
                  disabled={saving || loading}
                  size="large"
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                  onClick={handleSave}
                  disabled={saving || loading}
                  size="large"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !saving && !loading) {
                      handleSave();
                    }
                  }}
                >
                  {saving ? 'Saving...' : 'Save Settings'}
                </Button>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Preview Dialog */}
        <Dialog
          open={previewOpen}
          onClose={handleClosePreview}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              height: '90vh',
              maxHeight: '90vh'
            }
          }}
        >
          <DialogTitle
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              '&:focus': {
                outline: '2px solid',
                outlineColor: 'primary.main',
                outlineOffset: -2
              }
            }}
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                handleClosePreview();
              }
            }}
          >
            <Typography variant="h6">Print Format Preview</Typography>
            <IconButton
              edge="end"
              color="inherit"
              onClick={handleClosePreview}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleClosePreview();
                }
              }}
              aria-label="close preview"
              tabIndex={0}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 0, height: '100%', overflow: 'hidden' }}>
            {previewUrl && (
              <iframe
                src={previewUrl}
                style={{
                  width: '100%',
                  height: '100%',
                  border: 'none'
                }}
                title="Print Format Preview"
              />
            )}
          </DialogContent>
        </Dialog>
      </Container>
    </Layout>
  );
};

export default PrintSettingsPage;

